// Shown instead of a Premium part of the course: what it is, what Premium opens, and the next step for this visitor
// (create a free account, wait for a requested plan, or see the pricing).
// No locked content ever reaches the browser; this is all a locked page sends.

import type { FC, ReactNode } from 'react'
import type { L } from '@/prakrit/lib/lessons/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { Check, Crown, Hourglass, LoaderCircle, RefreshCw, Sparkles, WifiOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/client/session'
import { Bi } from '@/prakrit/components/common/bi'
import { PLAN_BY_ID } from '@/lib/plans'

export type PlanNoticeKind = 'lesson' | 'story' | 'set' | 'progress' | 'grammar' | 'tracking'

interface PlanNoticeProps {
    kind: PlanNoticeKind
    title?: L
    /** Smaller, inside a page section rather than the whole page. */
    compact?: boolean
    /** Kept for older callers; there is no trial any more, so it is never called. */
    onTrialStarted?: () => void
    /** The server could not check this visitor's Premium (the database did not answer): never offer Premium then. */
    unavailable?: boolean
    className?: string
}

const HEADING: Record<PlanNoticeKind, L> = {
    lesson: { hi: 'यह प्रीमियम पाठ है', en: 'This is a Premium lesson' },
    story: { hi: 'कथाएँ प्रीमियम में हैं', en: 'Stories are part of Premium' },
    set: { hi: 'यह प्रीमियम अभ्यास सेट है', en: 'This is a Premium practice set' },
    progress: { hi: 'पूरी प्रगति प्रीमियम में दिखती है', en: 'Full progress tracking is part of Premium' },
    grammar: { hi: 'व्याकरण प्रयोगशाला का यह भाग प्रीमियम है', en: 'This part of the Grammar Lab is Premium' },
    tracking: { hi: 'लीडरबोर्ड और पूरी प्रगति प्रीमियम में हैं', en: 'The leaderboard and full progress tracking are part of Premium' }
}

export const PLAN_FEATURES: L[] = [
    { hi: 'पूरा व्याकरण: सभी काल, विभक्ति, कृदन्त, सन्धि, समास', en: 'All of grammar: every tense, cases, participles, sandhi, compounds' },
    { hi: 'सभी १८ इकाइयाँ, सभी कथाएँ और बातचीत', en: 'All 18 units, every story and dialogue' },
    { hi: 'हर खेल के ५०० तक अभ्यास, सभी सेट', en: 'Up to 500 exercises in every game, all sets' },
    { hi: 'लीडरबोर्ड, कमज़ोर विषय और हर कौशल की प्रगति', en: 'The leaderboard, weak topics and progress in every skill' },
    { hi: 'नए पाठ और कथाएँ सबसे पहले', en: 'Early access to new lessons and stories' }
]

const BUTTON = 'inline-flex min-h-tap items-center justify-center gap-2 rounded-full px-6 py-2.5 font-semibold shadow-sm transition-colors disabled:opacity-60'

/**
 * Shown instead of a Premium notice while the account cannot be checked: a member must never be told to buy what they
 * have because of a database hiccup. "Try again" loads the page again, so the locked content comes when it can.
 */
export const AccessUnavailableNotice: FC<{ title?: L, compact?: boolean, className?: string }> = ({ title, compact = false, className }): ReactNode => {
    const router = useRouter()
    const { refresh } = useSession()
    const Heading = compact ? 'h2' : 'h1'

    return (
        <section
            aria-labelledby='access-unavailable'
            className={cn(
                'mx-auto flex w-full flex-col items-center gap-5 text-center',
                compact ? 'max-w-2xl rounded-3xl border border-saffron/40 bg-saffron/5 px-5 py-8' : 'max-w-xl px-4 py-14 sm:py-20',
                className
            )}
        >
            <span className='flex size-16 items-center justify-center rounded-full bg-saffron/15 ring-1 ring-saffron/40' aria-hidden='true'>
                <WifiOff className='size-8 text-saffron-strong' />
            </span>
            {title && (
                <p className='text-sm text-muted-foreground'>
                    <Bi hi={title.hi} en={title.en} layout='inline' className='justify-center' always />
                </p>
            )}
            <Heading id='access-unavailable' className={cn('font-heading font-bold leading-snug', compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl')}>
                <span lang='hi' className='block text-prakrit'>अभी आपकी सदस्यता जाँची नहीं जा सकी</span>
                <span lang='en' className='mt-1 block font-sans text-lg font-medium text-muted-foreground'>We cannot check your account right now</span>
            </Heading>
            <p className='max-w-md text-sm'>
                <Bi
                    hi='यदि आपके पास प्रीमियम है, तो वह सुरक्षित है; कुछ नहीं बदला। थोड़ी देर में फिर कोशिश करें।'
                    en='If you have Premium, it is safe and nothing has changed. Please try again in a moment.'
                />
            </p>
            <button
                type='button'
                onClick={() => {
                    // Check the account again (pages that decide in the browser) and load the page again (server ones).
                    void refresh()
                    router.replace(router.asPath)
                }}
                className={cn(BUTTON, 'bg-primary text-primary-foreground hover:bg-primary/85')}
            >
                <RefreshCw className='size-4' aria-hidden='true' />
                <Bi hi='फिर से कोशिश करें' en='Try again' layout='inline' always />
            </button>
        </section>
    )
}

/**
 * Shown instead of a lesson or story while its review states cannot be read (a database hiccup): nothing unreviewed
 * opens by error, and nobody is told the content is "awaiting review" when it is not.
 */
export const ContentUnavailableNotice: FC<{ kind: 'lesson' | 'story', title?: L }> = ({ kind, title }): ReactNode => {
    const router = useRouter()

    return (
        <section aria-labelledby='content-unavailable' className='mx-auto flex w-full max-w-xl flex-col items-center gap-5 px-4 py-14 text-center sm:py-20'>
            <span className='flex size-16 items-center justify-center rounded-full bg-saffron/15 ring-1 ring-saffron/40' aria-hidden='true'>
                <WifiOff className='size-8 text-saffron-strong' />
            </span>
            {title && (
                <p className='text-sm text-muted-foreground'>
                    <Bi hi={title.hi} en={title.en} layout='inline' className='justify-center' always />
                </p>
            )}
            <h1 id='content-unavailable' className='font-heading text-2xl font-bold leading-snug sm:text-3xl'>
                <span lang='hi' className='block text-prakrit'>{kind === 'lesson' ? 'यह पाठ अभी खुल नहीं पा रहा' : 'यह कथा अभी खुल नहीं पा रही'}</span>
                <span lang='en' className='mt-1 block font-sans text-lg font-medium text-muted-foreground'>
                    {kind === 'lesson' ? 'This lesson cannot be opened right now' : 'This story cannot be opened right now'}
                </span>
            </h1>
            <p className='max-w-md text-sm'>
                <Bi hi='आपकी प्रगति सुरक्षित है। थोड़ी देर में फिर कोशिश करें।' en='Your progress is safe. Please try again in a moment.' />
            </p>
            <button
                type='button'
                onClick={() => router.replace(router.asPath)}
                className={cn(BUTTON, 'bg-primary text-primary-foreground hover:bg-primary/85')}
            >
                <RefreshCw className='size-4' aria-hidden='true' />
                <Bi hi='फिर से कोशिश करें' en='Try again' layout='inline' always />
            </button>
        </section>
    )
}

export const PlanNotice: FC<PlanNoticeProps> = ({ kind, title, compact = false, unavailable = false, className }): ReactNode => {
    const router = useRouter()
    const { user, access, loading, unavailable: sessionUnavailable } = useSession()
    const signupHref = `/account?tab=signup&next=${encodeURIComponent(router.asPath)}`
    const pending = access.pendingRequest
    const Heading = compact ? 'h2' : 'h1'

    if (unavailable || sessionUnavailable || access.unavailable) return <AccessUnavailableNotice title={title} compact={compact} className={className} />

    return (
        <section
            aria-labelledby={`plan-notice-${kind}`}
            className={cn(
                'mx-auto flex w-full flex-col items-center gap-5 text-center',
                compact ? 'max-w-2xl rounded-3xl border border-saffron/40 bg-saffron/5 px-5 py-8' : 'max-w-xl px-4 py-14 sm:py-20',
                className
            )}
        >
            <span className='flex size-16 items-center justify-center rounded-full bg-saffron/15 ring-1 ring-saffron/40' aria-hidden='true'>
                <Crown className='size-8 text-saffron-strong' />
            </span>
            {title && (
                <p className='text-sm text-muted-foreground'>
                    <Bi hi={title.hi} en={title.en} layout='inline' className='justify-center' always />
                </p>
            )}
            <Heading id={`plan-notice-${kind}`} className={cn('font-heading font-bold leading-snug', compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl')}>
                <span lang='hi' className='block text-prakrit'>{HEADING[kind].hi}</span>
                <span lang='en' className='mt-1 block font-sans text-lg font-medium text-muted-foreground'>{HEADING[kind].en}</span>
            </Heading>

            <ul className='flex w-full max-w-md flex-col gap-2 text-left'>
                {PLAN_FEATURES.map(f => (
                    <li key={f.en} className='flex items-start gap-2.5'>
                        <Check className='mt-0.5 size-5 shrink-0 text-success' aria-hidden='true' />
                        <Bi hi={f.hi} en={f.en} className='gap-0' enClassName='text-sm text-muted-foreground' />
                    </li>
                ))}
            </ul>

            {loading ? (
                <LoaderCircle className='size-6 animate-spin text-saffron-strong' aria-label='लोड हो रहा है · Loading' />
            ) : (
                <div className='flex w-full flex-col items-center gap-3'>
                    {!user && (
                        <>
                            <Link href={signupHref} className={cn(BUTTON, 'bg-primary text-primary-foreground hover:bg-primary/85')}>
                                <Sparkles className='size-4' aria-hidden='true' />
                                <Bi hi='मुफ़्त खाता बनाइए' en='Create a free account' layout='inline' always />
                            </Link>
                            <p className='text-sm text-muted-foreground'>
                                <Bi hi='पहले से खाता है?' en='Already have an account?' layout='inline' className='justify-center' always />{' '}
                                <Link href={`/account?next=${encodeURIComponent(router.asPath)}`} className='font-semibold text-saffron-strong underline underline-offset-4'>
                                    <Bi hi='साइन इन' en='Sign in' layout='inline' always />
                                </Link>
                            </p>
                        </>
                    )}
                    {user && pending && (
                        <p className='flex items-start gap-2 rounded-xl bg-surface-muted px-4 py-3 text-left text-sm'>
                            <Hourglass className='mt-0.5 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                            <Bi
                                hi={`आपका "${PLAN_BY_ID[pending.plan].name.hi}" अनुरोध हमें मिल गया है। भुगतान की जानकारी के लिए हम आपसे संपर्क करेंगे।`}
                                en={`We have your request for "${PLAN_BY_ID[pending.plan].name.en}". We will contact you with the payment details.`}
                            />
                        </p>
                    )}
                    <Link
                        href='/pricing'
                        className={cn(BUTTON, user && !pending ? 'bg-primary text-primary-foreground hover:bg-primary/85' : 'border border-border bg-card hover:border-saffron/60 hover:bg-saffron/5')}
                    >
                        <Crown className='size-4' aria-hidden='true' />
                        <Bi hi='प्रीमियम देखें' en='See Premium' layout='inline' always />
                    </Link>
                </div>
            )}
        </section>
    )
}

/** Marks a Premium item on a free visitor's screen (hidden for members). */
export const PremiumMark: FC<{ className?: string }> = ({ className }): ReactNode => {
    const { access, loading, unavailable } = useSession()

    return loading || unavailable || access.full ? null : <PlanChip className={className} />
}

/** A small "Premium" chip for a locked lesson, unit, story, set or Grammar Lab page. */
export const PlanChip: FC<{ className?: string }> = ({ className }): ReactNode => (
    <span className={cn('inline-flex items-center gap-1 rounded-full bg-saffron/15 px-2.5 py-0.5 text-xs font-semibold text-saffron-strong', className)}>
        <Crown className='size-3.5' aria-hidden='true' />
        <span lang='hi' className='hindi'>प्रीमियम</span>
        <span lang='en' data-ex='en' className='font-medium opacity-80'>Premium</span>
    </span>
)

/** One line for visitors without an account where XP would show: XP, streaks and badges are free with an account. */
export const XpAccountNote: FC<{ className?: string }> = ({ className }): ReactNode => {
    const { user, loading, unavailable } = useSession()

    if (loading || unavailable || user) return null

    return (
        <p className={cn('flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-sm text-muted-foreground', className)}>
            <Sparkles className='size-4 text-saffron-strong' aria-hidden='true' />
            <Bi
                hi='मुफ़्त खाते से अंक, लड़ी और बैज गिने जाते हैं।'
                en='Create a free account to count XP, streaks and badges.'
                layout='inline'
            />
            <Link href='/account?tab=signup' className='font-semibold text-saffron-strong hover:underline'>
                <Bi hi='खाता बनाइए' en='Sign up free' layout='inline' always />
            </Link>
        </p>
    )
}

/** A small locked panel body (progress panels): what it would show, and the next step. */
export const PlanTeaser: FC<{ what: L }> = ({ what }): ReactNode => {
    const { user, unavailable } = useSession()

    if (unavailable) {
        return (
            <p className='flex items-start gap-2 rounded-xl border border-dashed border-saffron/50 bg-saffron/5 px-4 py-4 text-sm'>
                <WifiOff className='mt-0.5 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                <Bi hi='अभी आपकी सदस्यता जाँची नहीं जा सकी; थोड़ी देर में फिर देखें।' en='We cannot check your account right now. Please look again in a moment.' />
            </p>
        )
    }

    return (
        <div className='flex flex-col items-start gap-3 rounded-xl border border-dashed border-saffron/50 bg-saffron/5 px-4 py-4'>
            <p className='flex items-start gap-2 text-sm'>
                <Crown className='mt-0.5 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                <Bi hi={what.hi} en={what.en} />
            </p>
            <div className='flex flex-wrap gap-2'>
                {!user ? (
                    <Link href='/account?tab=signup&next=%2Fprogress' className='inline-flex min-h-tap items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/85'>
                        <Sparkles className='size-4' aria-hidden='true' />
                        <Bi hi='मुफ़्त खाता बनाइए' en='Create a free account' layout='inline' always />
                    </Link>
                ) : null}
                <Link href='/pricing' className='inline-flex min-h-tap items-center gap-1.5 rounded-full border border-border bg-card px-4 text-sm font-semibold hover:border-saffron/60'>
                    <Crown className='size-4 text-saffron-strong' aria-hidden='true' />
                    <Bi hi='प्रीमियम देखें' en='See Premium' layout='inline' always />
                </Link>
            </div>
        </div>
    )
}

/**
 * Shows its children to Premium members (a plan or staff) and the Premium notice to everyone else. Until the
 * session has loaded it shows a quiet placeholder, so locked content never flashes.
 */
export const PremiumGate: FC<{ kind: PlanNoticeKind, title?: L, children: ReactNode }> = ({ kind, title, children }): ReactNode => {
    const { access, loading } = useSession()

    if (loading) {
        return (
            <div role='status' className='flex min-h-[40vh] items-center justify-center'>
                <LoaderCircle className='size-7 animate-spin text-saffron-strong' aria-hidden='true' />
                <span className='sr-only'>लोड हो रहा है · Loading</span>
            </div>
        )
    }

    return access.full ? children : <PlanNotice kind={kind} title={title} />
}
